import React, { useMemo } from 'react';
import { Badge, Form, Table } from 'react-bootstrap';

/**
 * Badge for preview row match_status.
 * @param {string} status
 * @returns {JSX.Element}
 */
function MatchStatusBadge({ status, inInventory }) {
  if (inInventory || status === 'IN_INVENTORY') {
    return <Badge bg="success">In inventory</Badge>;
  }
  if (status === 'SHOP_CATALOG_ONLY') {
    return <Badge bg="warning" text="dark">Shop only</Badge>;
  }
  return <Badge bg="secondary">Not found</Badge>;
}

/**
 * Preview table with row selection for inventory-matched lines.
 *
 * @param {Object} props
 * @param {Object} props.previewData - API preview response
 * @param {Set<number>} props.selectedRowIndexes - row_index values selected
 * @param {Function} props.onSelectionChange - (Set<number>) => void
 */
const ImportPreviewTable = ({
  previewData,
  selectedRowIndexes,
  onSelectionChange,
}) => {
  const rows = useMemo(
    () => previewData?.rows ?? [],
    [previewData?.rows]
  );
  const summary = previewData?.summary ?? {};

  const inventoryRowIndexes = useMemo(
    () =>
      rows.filter(r => r.in_inventory === true).map(r => r.row_index),
    [rows]
  );

  const allInventorySelected =
    inventoryRowIndexes.length > 0 &&
    inventoryRowIndexes.every(idx => selectedRowIndexes.has(idx));

  const toggleRow = rowIndex => {
    const next = new Set(selectedRowIndexes);
    if (next.has(rowIndex)) next.delete(rowIndex);
    else next.add(rowIndex);
    onSelectionChange(next);
  };

  const toggleAllInventory = checked => {
    if (checked) {
      onSelectionChange(new Set(inventoryRowIndexes));
    } else {
      onSelectionChange(new Set());
    }
  };

  return (
    <div className="mb-4">
      <h5 className="mb-3">Preview results</h5>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <Badge bg="light" text="dark">
          Total: {summary.total_rows ?? 0}
        </Badge>
        <Badge bg="success">
          In inventory: {summary.matched_count ?? 0}
        </Badge>
        <Badge bg="secondary">
          Not found: {summary.not_found_count ?? 0}
        </Badge>
        <Badge bg="warning" text="dark">
          Shop only: {summary.shop_only_count ?? 0}
        </Badge>
        {(summary.duplicate_cat_nums ?? []).length > 0 ? (
          <Badge bg="danger">
            Duplicate CAT# in file: {summary.duplicate_cat_nums.join(', ')}
          </Badge>
        ) : null}
      </div>

      <div className="glass-table-wrap">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <Form.Check
                  type="checkbox"
                  checked={allInventorySelected}
                  disabled={inventoryRowIndexes.length === 0}
                  onChange={e => toggleAllInventory(e.target.checked)}
                  title="Select all inventory rows"
                  aria-label="Select all inventory rows"
                />
              </th>
              <th>Row</th>
              <th>CAT#</th>
              <th>Name (file)</th>
              <th>Qty</th>
              <th>Status</th>
              <th>Product</th>
              <th>Supplier</th>
              <th>Stock</th>
              <th>Warnings</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const selectable = row.in_inventory === true;
              const checked = selectedRowIndexes.has(row.row_index);
              return (
                <tr
                  key={row.row_index}
                  className={!selectable ? 'text-muted' : undefined}
                >
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={checked}
                      disabled={!selectable}
                      onChange={() => toggleRow(row.row_index)}
                      title={
                        selectable
                          ? 'Include in project link / order request'
                          : 'Not in inventory — cannot be included'
                      }
                      aria-label={`Select row ${row.row_index}`}
                    />
                  </td>
                  <td>{row.row_index}</td>
                  <td>{row.input_cat_num}</td>
                  <td>{row.input_name || '—'}</td>
                  <td>{row.input_quantity ?? 1}</td>
                  <td>
                    <MatchStatusBadge
                      status={row.match_status}
                      inInventory={row.in_inventory}
                    />
                  </td>
                  <td>{row.product_name || '—'}</td>
                  <td>{row.supplier_name || '—'}</td>
                  <td>{row.stock != null ? row.stock : '—'}</td>
                  <td className="small">
                    {(row.warnings ?? []).length
                      ? row.warnings.join('; ')
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      <p className="text-muted small mb-0">
        Only rows marked &quot;In inventory&quot; can be linked to a project or
        added to an order request. Shop-only and not-found rows are shown for
        reference.
      </p>
    </div>
  );
};

export default ImportPreviewTable;
